// @l-doc: doc/模板sku方案设计/modules/editImageNew/绘制文字工具字体流程.md
import Store from '@/utils/Store.js';

export const IMAGE_DATA_FONT_LIST_STORE_KEY = 'IMAGE_DATA_FONT_LIST_CACHE';
export const IMAGE_DATA_FONT_NOT_FOUND_MESSAGE = '没有找到对应字体文件';

const loadedFontPromises = new Map();
const IMAGE_DATA_TEXT_OBJECT_TYPE = 'IText';

/**
 * 规范化字体字符编码范围项。
 * @param {string} startValue - 起始 Unicode 编码。
 * @param {string} endValue - 结束 Unicode 编码。
 * @returns {Array<number>|null} 规范化后的编码范围，编码无效时返回 null。
 */
function normalizeImageDataFontRangeItem(startValue, endValue) {
  const start = parseInt(startValue, 16);
  const end = parseInt(endValue, 16);
  // 字符编码无法解析时忽略该范围项。
  if (!Number.isFinite(start) || !Number.isFinite(end)) {
    return null;
  }
  return [Math.min(start, end), Math.max(start, end)];
}

/**
 * 解析字体可输入字符区间。
 * @param {Object|string} fontRange - 字体字符区间，支持对象或字符串化 JSON。
 * @returns {Array<Array<number>>} 可输入字符 Unicode 编码区间。
 */
export function parseImageDataFontRange(fontRange) {
  if (!fontRange) {
    return [];
  }
  let data = fontRange;
  if (typeof fontRange === 'string') {
    data = JSON.parse(fontRange);
  }
  const ranges = Array.isArray(data && data.ranges) ? data.ranges : [];
  const singles = Array.isArray(data && data.singles) ? data.singles : [];
  return ranges
    .map((range) =>
      normalizeImageDataFontRangeItem(range && range[0], range && range[1])
    )
    .concat(singles.map((code) => normalizeImageDataFontRangeItem(code, code)))
    .filter(Boolean);
}

/**
 * 判断字符是否在字体支持区间内。
 * @param {string} char - 单个字符。
 * @param {Array<Array<number>>} ranges - Unicode 编码区间。
 * @returns {boolean} 是否支持该字符。
 */
export function isCharInImageDataFontRange(char, ranges) {
  if (!Array.isArray(ranges) || ranges.length === 0) {
    return true;
  }
  const codePoint = String(char || '').codePointAt(0);
  if (!Number.isFinite(codePoint)) {
    return true;
  }
  return ranges.some((range) => codePoint >= range[0] && codePoint <= range[1]);
}

/**
 * 过滤字体不支持的输入字符。
 * @param {string} text - 原始输入文本。
 * @param {Array<Array<number>>} ranges - Unicode 编码区间。
 * @returns {{text:string,removed:string}} 过滤结果。
 */
export function filterTextByImageDataFontRange(text, ranges) {
  if (!Array.isArray(ranges) || ranges.length === 0) {
    return {
      text: String(text || ''),
      removed: ''
    };
  }
  let nextText = '';
  let removed = '';
  Array.from(String(text || '')).forEach((char) => {
    if (isCharInImageDataFontRange(char, ranges)) {
      nextText += char;
    } else {
      removed += char;
    }
  });
  return {
    text: nextText,
    removed
  };
}

/**
 * 根据 fontId 匹配字体配置。
 * @param {Array<Object>} fontList - 字体列表。
 * @param {string} fontId - 字体 ID。
 * @returns {Object|null} 匹配到的字体配置。
 */
export function findImageDataFontById(fontList, fontId) {
  if (!Array.isArray(fontList) || !fontId) {
    return null;
  }
  return fontList.find((font) => font && font.fontId === fontId) || null;
}

/**
 * 读取本地缓存中的文字素材字体列表。
 * @returns {Array<Object>} 已缓存的字体列表。
 */
export function getStoredImageDataFontList() {
  const fontList = Store.get(IMAGE_DATA_FONT_LIST_STORE_KEY);
  return Array.isArray(fontList) ? fontList : [];
}

/**
 * 将接口返回的字体列表按 fontId 合并写入本地缓存。
 * @param {Array<Object>} fontList - 接口返回的字体列表。
 * @returns {Array<Object>} 合并后的字体列表。
 */
export function saveImageDataFontListToStore(fontList) {
  const incomingList = Array.isArray(fontList)
    ? fontList.filter((font) => font && font.fontId)
    : [];
  // 没有新字体列表时保持现有缓存，避免清空其它素材已缓存的字体。
  if (!incomingList.length) {
    return getStoredImageDataFontList();
  }
  const fontMap = new Map();
  getStoredImageDataFontList().forEach((font) => {
    // 本地缓存中无 fontId 的异常数据不参与后续匹配。
    if (font && font.fontId) {
      fontMap.set(font.fontId, font);
    }
  });
  incomingList.forEach((font) => {
    fontMap.set(font.fontId, font);
  });
  const nextFontList = Array.from(fontMap.values());
  Store.set(IMAGE_DATA_FONT_LIST_STORE_KEY, nextFontList);
  return nextFontList;
}

/**
 * 从本地缓存中按 fontId 匹配字体配置。
 * @param {string} fontId - 字体 ID。
 * @returns {Object|null} 匹配到的字体配置。
 */
export function findStoredImageDataFontById(fontId) {
  return findImageDataFontById(getStoredImageDataFontList(), fontId);
}

/**
 * 从文字样式中读取 fontId。
 * @param {Object} style - imageData 文字样式。
 * @returns {string} 字体 ID。
 */
export function getImageDataFontIdFromStyle(style) {
  return String((style && style.fontId) || '').trim();
}

/**
 * 获取字体包地址。
 * @param {Object} fontInfo - 字体配置。
 * @returns {string} 字体包地址。
 */
export function getImageDataFontUrl(fontInfo) {
  return (fontInfo && (fontInfo.woff2Url || fontInfo.ttfUrl)) || '';
}

/**
 * 转义调试字体 class 选择器。
 * @param {string} className - 原始 class 名。
 * @returns {string} 可写入 CSS 选择器的 class 名。
 */
function escapeImageDataFontClassName(className) {
  // 浏览器支持 CSS.escape 时优先使用标准转义。
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(className);
  }
  return String(className).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
}

/**
 * 生成文字素材字体 class 名。
 * @param {string} fontId - 字体 ID。
 * @returns {string} 字体 class 名。
 */
export function buildImageDataFontClassName(fontId) {
  return fontId ? `font-${fontId}` : '';
}

/**
 * 注入字体调试 class，便于在 DOM 上验证字体是否生效。
 * @param {Object} fontInfo - 字体配置。
 * @returns {void}
 */
function injectImageDataFontDebugClass(fontInfo) {
  // 非浏览器环境无需注入样式。
  if (typeof document === 'undefined') {
    return;
  }
  const fontId = fontInfo && fontInfo.fontId;
  const fontUrl = getImageDataFontUrl(fontInfo);
  // 字体配置不完整时交给加载流程统一报错。
  if (!fontId || !fontUrl) {
    return;
  }
  const styleId = `image-data-font-debug-${fontId}`;
  const debugClassName = `f-${fontId}`;
  const inputClassName = buildImageDataFontClassName(fontId);
  let styleElement = document.getElementById(styleId);
  // 首次加载该字体时创建调试样式节点。
  if (!styleElement) {
    styleElement = document.createElement('style');
    styleElement.id = styleId;
    document.head.appendChild(styleElement);
  }
  const cssFontFamily = `"${String(fontId).replace(/"/g, '\\"')}"`;
  styleElement.textContent = [
    `@font-face { font-family: ${cssFontFamily}; src: url("${fontUrl}"); }`,
    `.${escapeImageDataFontClassName(
      debugClassName
    )} { font-family: ${cssFontFamily}, sans-serif !important; }`,
    `.${escapeImageDataFontClassName(
      inputClassName
    )} { font-family: ${cssFontFamily}, sans-serif !important; }`
  ].join('\n');
}

/**
 * 生成写入 imageData customData 的字体信息。
 * @param {Object} fontInfo - 字体配置。
 * @returns {{fontId:string,ttfUrl:string,woff2Url:string}} 绘制需要的字体信息。
 */
export function buildImageDataCustomFontInfo(fontInfo) {
  return {
    fontId: fontInfo.fontId,
    ttfUrl: fontInfo.ttfUrl || '',
    woff2Url: fontInfo.woff2Url || ''
  };
}

/**
 * 读取 imageData object 上的 customData.fontInfo。
 * @param {Object} object - imageData.objects 中的单个对象。
 * @returns {Object|null} 文字对象字体信息。
 */
export function getImageDataObjectFontInfo(object) {
  return (object && object.customData && object.customData.fontInfo) || null;
}

/**
 * 注册并加载字体包。
 * @param {Object} fontInfo - 字体配置。
 * @returns {Promise<string>} 可用于 canvas 的 fontFamily。
 */
export async function loadImageDataFont(fontInfo) {
  const fontId = fontInfo && fontInfo.fontId;
  const fontUrl = getImageDataFontUrl(fontInfo);
  if (!fontId || !fontUrl) {
    throw new Error(IMAGE_DATA_FONT_NOT_FOUND_MESSAGE);
  }
  injectImageDataFontDebugClass(fontInfo);
  const cacheKey = `${fontId}:${fontUrl}`;
  if (!loadedFontPromises.has(cacheKey)) {
    loadedFontPromises.set(
      cacheKey,
      (async () => {
        if (
          typeof FontFace === 'undefined' ||
          typeof document === 'undefined' ||
          !document.fonts
        ) {
          return fontId;
        }
        const cssFontFamily = `"${String(fontId).replace(/"/g, '\\"')}"`;
        try {
          const fontFace = new FontFace(fontId, `url("${fontUrl}")`);
          const loadedFont = await fontFace.load();
          document.fonts.add(loadedFont);
          await document.fonts.load(`12px ${cssFontFamily}`);
          return fontId;
        } catch (error) {
          throw new Error(IMAGE_DATA_FONT_NOT_FOUND_MESSAGE);
        }
      })()
    );
  }
  return await loadedFontPromises.get(cacheKey);
}

/**
 * 补齐 imageData 中文字对象的 customData.fontInfo。
 * @param {Object} options - 参数对象。
 * @param {Object} options.imageData - 解析后的 imageData。
 * @param {Array<Object>} options.fontList - 字体列表。
 * @returns {Object} 补齐字体信息后的 imageData。
 */
export function hydrateImageDataFontInfo({ imageData, fontList }) {
  const objects = Array.isArray(imageData && imageData.objects)
    ? imageData.objects
    : [];
  objects.forEach((object) => {
    // 只有 IText 对象需要补齐字体绘制信息。
    if (!object || object.type !== IMAGE_DATA_TEXT_OBJECT_TYPE) {
      return;
    }
    const fontId = getImageDataFontIdFromStyle(
      object.text && object.text.style
    );
    // 新格式 IText 必须提供 fontId。
    if (!fontId) {
      throw new Error(IMAGE_DATA_FONT_NOT_FOUND_MESSAGE);
    }
    if (getImageDataObjectFontInfo(object)) {
      return;
    }
    const fontInfo = findImageDataFontById(fontList, fontId);
    // 有 fontId 但没有匹配到字体配置时，必须阻断，避免系统字体静默兜底。
    if (!fontInfo) {
      console.error('[hydrateImageDataFontInfo] fontId未匹配到fontList', {
        missingFontId: fontId,
        fontList,
        object
      });
      throw new Error(IMAGE_DATA_FONT_NOT_FOUND_MESSAGE);
    }
    // customData 是文字对象承载扩展信息的最外层字段。
    if (!object.customData) {
      object.customData = {};
    }
    object.customData.fontInfo = buildImageDataCustomFontInfo(fontInfo);
  });
  return imageData;
}

/**
 * 确保 imageData 中用到的字体已加载。
 * @param {Object} options - 参数对象。
 * @param {Object} options.imageData - 解析后的 imageData。
 * @returns {Promise<void>}
 */
export async function ensureImageDataFontsLoaded({ imageData }) {
  const objects = Array.isArray(imageData && imageData.objects)
    ? imageData.objects
    : [];
  const fontInfoMap = new Map();
  objects
    .filter((item) => item && item.type === IMAGE_DATA_TEXT_OBJECT_TYPE)
    .forEach((item) => {
      const fontId = getImageDataFontIdFromStyle(item.text && item.text.style);
      if (!fontId) {
        throw new Error(IMAGE_DATA_FONT_NOT_FOUND_MESSAGE);
      }
      const fontInfo = getImageDataObjectFontInfo(item);
      // 有 fontId 但没有字体文件信息时，必须阻断，避免系统字体静默兜底。
      if (!fontInfo || !fontInfo.fontId) {
        console.error('[ensureImageDataFontsLoaded] 文字对象缺少字体文件信息', {
          missingFontId: fontId,
          object: item
        });
        throw new Error(IMAGE_DATA_FONT_NOT_FOUND_MESSAGE);
      }
      fontInfoMap.set(fontInfo.fontId, fontInfo);
    });
  await Promise.all(
    Array.from(fontInfoMap.values()).map((fontInfo) =>
      loadImageDataFont(fontInfo)
    )
  );
}

/**
 * 获取文字对象实际绘制使用的 fontFamily。
 * @param {Object} object - imageData.objects 中的单个文字对象。
 * @returns {string} fontFamily。
 */
export function resolveImageDataTextFontFamily(object) {
  const style = (object && object.text && object.text.style) || {};
  const fontId = getImageDataFontIdFromStyle(style);
  if (!fontId) {
    throw new Error(IMAGE_DATA_FONT_NOT_FOUND_MESSAGE);
  }
  const fontInfo = getImageDataObjectFontInfo(object);
  // 有 fontId 但没有字体文件信息时，必须阻断，避免系统字体静默兜底。
  if (!fontInfo || !fontInfo.fontId) {
    console.error('[resolveImageDataTextFontFamily] 文字对象缺少字体文件信息', {
      missingFontId: fontId,
      object
    });
    throw new Error(IMAGE_DATA_FONT_NOT_FOUND_MESSAGE);
  }
  return fontInfo.fontId;
}
